/**
 * Quick View Close Handler
 * Fecha o quick-view automaticamente após adicionar produto ao carrinho
 * E garante que os botões de fechar (X e overlay) funcionem corretamente
 */

class QuickViewCloseHandler {
  constructor() {
    this.isClosing = false; // Flag para evitar loops
    this.init();
  }

  init() {
    // Aguardar um pouco para garantir que o tema carregou
    setTimeout(() => {
      // Escutar evento de sucesso ao adicionar ao carrinho
      document.addEventListener('cart:updated', (event) => {
        if (!this.isClosing) {
          this.closeAllQuickViews();
        }
      }, { once: false });

      // Escutar eventos do pubsub (sistema de eventos do tema)
      if (window.theme && window.theme.pubsub && window.theme.pubsub.PUB_SUB_EVENTS) {
        try {
          window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
            if (!this.isClosing) {
              this.closeAllQuickViews();
            }
          });
        } catch (e) {
          // Ignorar erro se pubsub não estiver disponível
        }
      }

      // Configurar botões de fechar
      this.setupCloseButtons();

      // Configurar overlay para fechar ao clicar fora
      this.setupOverlayClose();
    }, 500);
  }

  /**
   * Fecha todos os quick-views abertos
   */
  closeAllQuickViews() {
    if (this.isClosing) return; // Evitar loops
    
    this.isClosing = true;
    
    const quickViews = document.querySelectorAll('quick-view[open], quick-view[active], .quick-view[open], .quick-view[active], quick-view:not([hidden]), .quick-view:not([hidden])');
    
    quickViews.forEach((quickView) => {
      // Verificar se realmente está visível
      const isVisible = quickView.hasAttribute('open') || 
                        quickView.hasAttribute('active') || 
                        quickView.classList.contains('active') ||
                        quickView.classList.contains('open');
      
      if (isVisible) {
        this.closeQuickView(quickView);
      }
    });
    
    // Resetar flag após um delay
    setTimeout(() => {
      this.isClosing = false;
    }, 300);
  }

  /**
   * Fecha um quick-view específico
   */
  closeQuickView(quickView) {
    if (!quickView) return;

    // Verificar se realmente está aberto antes de fechar
    const isOpen = quickView.hasAttribute('open') || 
                  quickView.hasAttribute('active') || 
                  quickView.classList.contains('active') ||
                  (!quickView.hasAttribute('hidden') && quickView.style.display !== 'none');
    
    if (!isOpen) {
      return; // Já está fechado, não fazer nada
    }

    // Estratégia 1: Tentar usar método hide() se existir (DrawerElement)
    if (typeof quickView.hide === 'function') {
      try {
        quickView.hide();
        return;
      } catch (e) {
        // Continuar para outras estratégias
      }
    }

    // Estratégia 2: Remover atributos que mantêm o modal aberto
    quickView.removeAttribute('open');
    quickView.removeAttribute('active');
    quickView.setAttribute('hidden', '');
    
    // Estratégia 3: Remover classes que indicam estado ativo
    quickView.classList.remove('active', 'open');
    // Apenas definir pointer-events, não display (deixar CSS do tema controlar)
    quickView.style.pointerEvents = 'none';
    
    // Estratégia 4: Atualizar aria-expanded em todos os controles
    const quickViewId = quickView.id;
    if (quickViewId) {
      const controls = document.querySelectorAll(`[aria-controls="${quickViewId}"]`);
      controls.forEach((control) => {
        control.setAttribute('aria-expanded', 'false');
      });
    }

    // Estratégia 5: Fechar overlay
    const overlay = quickView.querySelector('overlay-element, .overlay');
    if (overlay) {
      overlay.setAttribute('aria-expanded', 'false');
      overlay.classList.add('invisible', 'opacity-0');
      overlay.style.pointerEvents = 'none';
    }

    // Estratégia 6: Disparar evento customizado para outros listeners
    quickView.dispatchEvent(new CustomEvent('quick-view:close', {
      bubbles: true,
      cancelable: true
    }));
  }

  /**
   * Configura os botões de fechar (X)
   */
  setupCloseButtons() {
    // Usar event delegation para capturar cliques em botões de fechar
    // IMPORTANTE: Não interferir com botões que ABREM o quick-view
    document.addEventListener('click', (e) => {
      // Verificar se é um botão de FECHAR (não de abrir)
      const closeButton = e.target.closest('.drawer__close, button.button--close');
      
      // Se não for botão de fechar, deixar o evento passar normalmente (para abrir o modal)
      if (!closeButton) {
        return;
      }
      
      if (closeButton && closeButton.getAttribute('aria-controls')) {
        const quickViewId = closeButton.getAttribute('aria-controls');
        const quickView = document.getElementById(quickViewId);
        
        // Verificar se o quick-view existe e está aberto
        if (quickView && (quickView.classList.contains('quick-view') || quickView.tagName === 'QUICK-VIEW')) {
          // Verificar se está realmente aberto antes de fechar
          const isOpen = quickView.hasAttribute('open') || 
                        quickView.hasAttribute('active') || 
                        quickView.classList.contains('active') ||
                        !quickView.hasAttribute('hidden');
          
          if (isOpen && !this.isClosing) {
            e.preventDefault();
            e.stopPropagation();
            // NÃO usar stopImmediatePropagation para não bloquear outros handlers necessários
            this.closeQuickView(quickView);
          }
        }
      }
    }, false); // Usar bubble phase (não capture) para não interferir com hover-button
  }

  /**
   * Configura o overlay para fechar ao clicar fora
   */
  setupOverlayClose() {
    // Usar event delegation para capturar cliques no overlay
    document.addEventListener('click', (e) => {
      const overlay = e.target.closest('overlay-element, .overlay');
      
      if (overlay && overlay.getAttribute('aria-controls')) {
        const quickViewId = overlay.getAttribute('aria-controls');
        const quickView = document.getElementById(quickViewId);
        
        // Verificar se o clique foi diretamente no overlay (não em filhos)
        // E se o quick-view está aberto
        if (quickView && (quickView.classList.contains('quick-view') || quickView.tagName === 'QUICK-VIEW')) {
          const isOpen = quickView.hasAttribute('open') || 
                        quickView.hasAttribute('active') || 
                        quickView.classList.contains('active') ||
                        !quickView.hasAttribute('hidden');
          
          // Verificar se o clique foi diretamente no overlay (não em elementos filhos do drawer)
          const clickedOnOverlay = e.target === overlay || 
                                   e.target.classList.contains('overlay') ||
                                   (overlay.contains(e.target) && !quickView.querySelector('.drawer__inner')?.contains(e.target));
          
          if (isOpen && clickedOnOverlay && !this.isClosing) {
            e.preventDefault();
            e.stopPropagation();
            // NÃO usar stopImmediatePropagation
            this.closeQuickView(quickView);
          }
        }
      }
    }, false); // Usar bubble phase
  }
}

// Inicializar quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuickViewCloseHandler();
  });
} else {
  new QuickViewCloseHandler();
}

